/**
 * WerkstattAuslageSection — Issue #583
 *
 * Zeigt in der Werkstatt-Akte-Detailansicht:
 *   1. Orange „Auslage SV-Büro"-Pill (Header)
 *   2. Erstattungs-Sektion mit Status-Badge, Betrag, Datum, Abrechnungsschreiben-Link
 *   3. CTA „Erstattung verbuchen" → Modal mit PDF-Upload + Betrag/Datum
 *
 * Wenn kein Abrechnungsschreiben vorhanden: Begründung (Freitext) als Pflichtfeld.
 * Beim Speichern: Dokument automatisch in beide Akten kopiert (kategorie='versicherungs_abrechnung').
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, Calendar, Check, ExternalLink, FileText, Loader2,
  Receipt, Upload, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { berechneErstattungsStatus } from '@/lib/erstattung-status';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RechnungAuslage {
  id: string;
  brutto_betrag: number;
  erstattungsbetrag: number | null;
  erstattung_eingang_datum: string | null;
  erstattung_status: string | null;
  abrechnungsschreiben_dokument_id: string | null;
  abrechnungsschreiben_url: string | null;
}

interface WerkstattAuslageSectionProps {
  werkstattAkteId: string;
  accessToken: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatEuro(val: number | null | undefined): string {
  if (val == null) return '—';
  return Number(val).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
}

function formatDate(val: string | null | undefined): string {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('de-DE');
  } catch {
    return val;
  }
}

const STATUS_BADGE_STYLE: Record<string, string> = {
  noch_nicht_erstattet: 'bg-gray-100 text-gray-600',
  teil_erstattet: 'bg-yellow-100 text-yellow-700',
  voll_erstattet: 'bg-green-100 text-green-700',
  abgewiesen: 'bg-red-100 text-red-700',
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ErstattungModalProps {
  rechnung: RechnungAuslage;
  werkstattAkteId: string;
  accessToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function ErstattungModal({
  rechnung,
  werkstattAkteId,
  accessToken,
  onClose,
  onSaved,
}: ErstattungModalProps) {
  const [betrag, setBetrag] = useState<string>(
    rechnung.erstattungsbetrag != null ? String(rechnung.erstattungsbetrag) : '',
  );
  const [datum, setDatum] = useState<string>(rechnung.erstattung_eingang_datum ?? '');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [keinSchreiben, setKeinSchreiben] = useState(false);
  const [begruendung, setBegruendung] = useState('');
  const [abgewiesen, setAbgewiesen] = useState(
    rechnung.erstattung_status === 'abgewiesen',
  );
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isValid =
    (pdfFile !== null || keinSchreiben) &&
    (!keinSchreiben || begruendung.trim().length > 0) &&
    (abgewiesen || (betrag !== '' && datum !== ''));

  async function handleSave() {
    if (!isValid) return;
    setSaving(true);

    try {
      let storagePfad: string | null = null;
      let dateiGroesse: number | null = null;

      if (pdfFile) {
        const ts = Date.now();
        storagePfad = `abrechnungsschreiben/${werkstattAkteId}/${ts}_${pdfFile.name}`;
        dateiGroesse = pdfFile.size;

        const { error: uploadErr } = await supabase.storage
          .from('dokumente')
          .upload(storagePfad, pdfFile, { upsert: false });

        if (uploadErr) {
          toast.error(`Hochladen fehlgeschlagen: ${uploadErr.message}`);
          setSaving(false);
          return;
        }
      }

      const brutto = rechnung.brutto_betrag;
      const betragNum = abgewiesen ? null : parseFloat(betrag.replace(',', '.'));
      let erstattungStatus: 'teil_erstattet' | 'voll_erstattet' | 'abgewiesen';
      if (abgewiesen) {
        erstattungStatus = 'abgewiesen';
      } else if (betragNum != null && betragNum >= brutto) {
        erstattungStatus = 'voll_erstattet';
      } else {
        erstattungStatus = 'teil_erstattet';
      }

      const authHeader: Record<string, string> = accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : {};

      const res = await fetch('/api/werkstatt-auslage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          action: 'verbucheErstattung',
          werkstattAkteId,
          rechnungId: rechnung.id,
          erstattungsbetrag: betragNum,
          datum: abgewiesen ? null : datum || null,
          erstattungStatus,
          storagePfad,
          dateiName: pdfFile?.name ?? null,
          dateiGroesse,
          keinSchreiben,
          begruendung: keinSchreiben ? begruendung : null,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.ok) {
        toast.error(json.error ?? 'Fehler beim Speichern');
        setSaving(false);
        return;
      }

      toast.success('Erstattung wurde verbucht');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unbekannter Fehler');
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="erstattung-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-orange-500" />
            Erstattung verbuchen
          </h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Abgewiesen-Toggle */}
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-red-600"
            checked={abgewiesen}
            onChange={(e) => setAbgewiesen(e.target.checked)}
          />
          <span className="text-sm font-medium text-gray-700">
            Erstattung wurde abgewiesen
          </span>
        </label>

        {!abgewiesen && (
          <>
            {/* Erstattungsbetrag */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Erstattungsbetrag (€) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={betrag}
                  onChange={(e) => setBetrag(e.target.value)}
                  placeholder={`max. ${formatEuro(rechnung.brutto_betrag)}`}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
              </div>
            </div>

            {/* Datum */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Eingangsdatum *
              </label>
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <input
                  type="date"
                  value={datum}
                  onChange={(e) => setDatum(e.target.value)}
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400"
                />
              </div>
            </div>
          </>
        )}

        {/* PDF-Upload */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Abrechnungsschreiben (PDF) *
          </label>
          {pdfFile ? (
            <div className="flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg border border-blue-200">
              <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-xs text-blue-800 truncate flex-1">{pdfFile.name}</span>
              <button
                onClick={() => setPdfFile(null)}
                className="p-0.5 hover:text-red-500 text-gray-400 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => inputRef.current?.click()}
              disabled={keinSchreiben}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-orange-300 hover:text-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-3.5 h-3.5" />
              PDF hochladen
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
          />

          {/* Kein Schreiben erhalten */}
          <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 rounded border-gray-300"
              checked={keinSchreiben}
              onChange={(e) => {
                setKeinSchreiben(e.target.checked);
                if (e.target.checked) setPdfFile(null);
              }}
            />
            <span className="text-xs text-gray-500">Kein Schreiben erhalten</span>
          </label>

          {keinSchreiben && (
            <textarea
              rows={2}
              value={begruendung}
              onChange={(e) => setBegruendung(e.target.value)}
              placeholder="Begründung (Pflichtfeld)"
              className="mt-2 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 resize-none"
            />
          )}
        </div>

        {!isValid && (
          <p className="flex items-center gap-1.5 text-xs text-amber-600">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            Bitte alle Pflichtfelder ausfüllen.
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || saving}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

export default function WerkstattAuslageSection({
  werkstattAkteId,
  accessToken,
}: WerkstattAuslageSectionProps) {
  const [rechnung, setRechnung] = useState<RechnungAuslage | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const lastFetchedId = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!werkstattAkteId || lastFetchedId.current === werkstattAkteId) return;
    lastFetchedId.current = werkstattAkteId;

    setLoading(true);
    try {
      const authHeader: Record<string, string> = accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : {};
      const res = await fetch(
        `/api/werkstatt-auslage?action=auslage&id=${werkstattAkteId}`,
        { headers: authHeader },
      );
      const json = await res.json();
      setRechnung(json.rechnung ?? null);
    } catch {
      setRechnung(null);
    } finally {
      setLoading(false);
    }
  }, [werkstattAkteId, accessToken]);

  const reload = useCallback(() => {
    lastFetchedId.current = null;
    load();
  }, [load]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!rechnung) return null;

  const statusInfo = berechneErstattungsStatus({
    auslage: true,
    erstattungsbetrag: rechnung.erstattungsbetrag,
    bruttoBetrag: rechnung.brutto_betrag,
    erstattungStatus: rechnung.erstattung_status,
  });

  const badgeStyle = STATUS_BADGE_STYLE[statusInfo.status] ?? 'bg-gray-100 text-gray-600';

  return (
    <>
      {/* Orange Auslage-Pill */}
      <div
        data-testid="auslage-sv-buro-pill"
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-xs font-semibold"
      >
        <Receipt className="w-3.5 h-3.5" />
        Auslage SV-Büro
      </div>

      {/* Erstattungs-Sektion */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Erstattung Versicherung
          </h2>
          <button
            data-testid="erstattung-verbuchen-btn"
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Receipt className="w-3.5 h-3.5" />
            Erstattung verbuchen
          </button>
        </div>

        {/* Status-Badge */}
        <div className="flex flex-wrap items-center gap-3">
          <span
            data-testid="erstattung-status-badge"
            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${badgeStyle}`}
          >
            {statusInfo.status === 'noch_nicht_erstattet' && 'noch nicht erstattet'}
            {statusInfo.status === 'teil_erstattet' && 'teil erstattet'}
            {statusInfo.status === 'voll_erstattet' && 'voll erstattet'}
            {statusInfo.status === 'abgewiesen' && 'abgewiesen'}
          </span>

          {rechnung.erstattungsbetrag != null && (
            <span className="text-sm font-semibold text-gray-800">
              {formatEuro(rechnung.erstattungsbetrag)}
            </span>
          )}

          {rechnung.erstattung_eingang_datum && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(rechnung.erstattung_eingang_datum)}
            </span>
          )}
        </div>

        {/* Rechnungs-Brutto */}
        <p className="text-xs text-gray-400">
          Rechnungsbetrag: {formatEuro(rechnung.brutto_betrag)}
        </p>

        {/* Abrechnungsschreiben-Link */}
        {rechnung.abrechnungsschreiben_url && (
          <a
            data-testid="abrechnungsschreiben-link"
            href={rechnung.abrechnungsschreiben_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Abrechnungsschreiben
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <ErstattungModal
          rechnung={rechnung}
          werkstattAkteId={werkstattAkteId}
          accessToken={accessToken}
          onClose={() => setModalOpen(false)}
          onSaved={reload}
        />
      )}
    </>
  );
}
